<template>
  <div class="product-list">
    <div class="page-header">
      <h2>商品列表</h2>
      <a-button type="primary" @click="$router.push('/products/create')">
        <template #icon>
          <PlusOutlined />
        </template>
        创建商品
      </a-button>
    </div>
    
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="商品名称" name="productIds">
          <a-select
            v-model:value="searchForm.productIds"
            mode="multiple"
            placeholder="请选择或搜索商品"
            style="min-width: 260px; max-width: 480px;"
            :options="productOptions"
            :loading="productOptionsLoading"
            :filter-option="filterProductOption"
            :max-tag-count="2"
            allow-clear
            option-filter-prop="label"
          />
        </a-form-item>
        
        <a-form-item label="商品分类" name="categoryId">
          <a-select
            v-model:value="searchForm.categoryId"
            placeholder="请选择分类"
            style="width: 200px"
            allowClear
          >
            <a-select-option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="商品状态" name="status">
          <a-select
            v-model:value="searchForm.status"
            placeholder="请选择状态"
            style="width: 120px"
            allowClear
          >
            <a-select-option :value="1">上架</a-select-option>
            <a-select-option :value="0">下架</a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            搜索
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>
    
    <a-card>
      <a-table
        :columns="columns"
        :data-source="productList"
        :pagination="pagination"
        :loading="loading"
        @change="handleTableChange"
        row-key="id"
        :scroll="{ x: 950 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'image'">
            <a-image
              v-if="record.imageUrls && record.imageUrls.length > 0"
              :width="60"
              :height="60"
              :src="$thumb(record.imageUrls[0])"
              :preview="false"
              style="border-radius: 4px; object-fit: cover;"
            />
            <div v-else class="no-image">暂无图片</div>
          </template>
          
          <template v-else-if="column.key === 'price'">
            <div>
              <div>售价: ¥{{ record.salePrice || 0 }}</div>
              <div style="color: #666; font-size: 12px">
                成本: ¥{{ record.costPrice || 0 }}
              </div>
            </div>
          </template>
          
          <template v-else-if="column.key === 'stock'">
            <div style="display: flex; flex-direction: column; gap: 4px; max-width: 200px;">
              <div v-if="record.stockItems && record.stockItems.length > 0">
                <div
                  v-for="item in aggregateStock(record.stockItems)"
                  :key="`${item.color}-${item.size}`"
                  style="display: flex; align-items: center; flex-wrap: wrap;"
                >
                  <a-tag
                    :color="item.stockQuantity > 10 ? 'green' : item.stockQuantity > 0 ? 'orange' : 'red'"
                    style="margin: 2px 0; font-size: 12px;"
                  >
                    {{ item.color || '默认' }} {{ item.size || '默认' }}: {{ item.stockQuantity }}
                  </a-tag>
                  <a-tag
                    v-if="item.producingQuantity > 0"
                    color="blue"
                    style="margin: 2px 0 2px 2px; font-size: 12px;"
                    title="预生产单中尚未完成的数量"
                  >
                    计划 {{ item.producingQuantity }}
                  </a-tag>
                </div>
              </div>
              <div v-else style="color: #999; font-size: 12px;">暂无库存</div>
            </div>
          </template>
          
          <template v-else-if="column.key === 'totalStock'">
            <div style="text-align: center;">
              <a-tag
                :color="record.totalStockQuantity > 20 ? 'green' : record.totalStockQuantity > 0 ? 'orange' : 'red'"
                style="font-weight: 500; font-size: 14px;"
              >
                {{ record.totalStockQuantity || 0 }}
              </a-tag>
            </div>
          </template>

          <template v-else-if="column.key === 'salesData'">
            <div style="text-align: center; font-size: 12px; line-height: 1.4;">
              <div>销售: <span style="color: #1890ff; font-weight: 500;">{{ record.totalSalesQuantity || 0 }}</span></div>
              <div>退货: <span style="color: #fa8c16; font-weight: 500;">{{ record.totalReturnQuantity || 0 }}</span></div>
              <div style="margin-top: 4px;">
                净销售:
                <a-tag
                  :color="((record.netSalesQuantity !== undefined ? record.netSalesQuantity : (record.totalSalesQuantity || 0) - (record.totalReturnQuantity || 0)) > 0) ? 'green' : ((record.netSalesQuantity !== undefined ? record.netSalesQuantity : (record.totalSalesQuantity || 0) - (record.totalReturnQuantity || 0)) === 0) ? 'default' : 'red'"
                  size="small"
                  style="font-weight: 500;"
                >
                  {{ record.netSalesQuantity !== undefined ? record.netSalesQuantity : (record.totalSalesQuantity || 0) - (record.totalReturnQuantity || 0) }}
                </a-tag>
              </div>
            </div>
          </template>

          
          <template v-else-if="column.key === 'status'">
            <a-tag :color="record.status == 1 ? 'green' : 'red'">
              {{ record.status == 1 ? '上架' : '下架' }}
            </a-tag>
          </template>
          
          <template v-else-if="column.key === 'action'">
            <a-space>
              <a-button
                type="link"
                size="small"
                style="color: #52c41a"
                @click="handleStockIn(record)"
              >
                入库
              </a-button>
              <a-button
                type="link"
                size="small"
                style="color: #ff7875"
                @click="handleStockOut(record)"
              >
                出库
              </a-button>
              <a-button
                type="link"
                size="small"
                @click="handleEdit(record)"
              >
                编辑
              </a-button>
              <a-popconfirm
                title="确定要删除这个商品吗？"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>
    
    <!-- 仓库选择弹窗 -->
    <a-modal
      v-model:visible="warehouseModalVisible"
      :title="modalTitle"
      @ok="handleWarehouseConfirm"
      @cancel="warehouseModalVisible = false"
      :confirm-loading="modalLoading"
    >
      <a-form :model="warehouseForm" layout="vertical">
        <a-row :gutter="16">
          <a-col :span="12">
            <a-form-item label="操作类型" required>
              <a-select
                v-model:value="warehouseForm.reasonType"
                placeholder="请选择操作类型"
              >
                <a-select-option v-if="currentOperation === 'in'" value="return">退货入库</a-select-option>
                <a-select-option v-if="currentOperation === 'in'" value="production">生产入库</a-select-option>
                <a-select-option v-if="currentOperation === 'out'" value="sales">销售出库</a-select-option>
                <a-select-option value="other">其他</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item label="记录日期" required>
              <a-date-picker
                v-model:value="warehouseForm.recordDate"
                placeholder="请选择记录日期"
                style="width: 100%"
                format="YYYY-MM-DD"
                :disabled-date="disabledDate"
              />
            </a-form-item>
          </a-col>
        </a-row>
        
        <a-form-item label="选择仓库" required>
          <a-select
            v-model:value="warehouseForm.warehouseId"
            placeholder="请选择仓库"
            :disabled="warehouses.length === 1"
            @change="onWarehouseChange"
          >
            <a-select-option
              v-for="warehouse in warehouses"
              :key="warehouse.id"
              :value="warehouse.id"
            >
              {{ warehouse.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="选择SKU" required>
          <div v-if="availableSkus.length > 0" style="border: 1px solid #d9d9d9; border-radius: 6px; padding: 16px;">
            <!-- 表头 -->
            <div style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; font-weight: 500; color: #666;">
              <div style="text-align: center;">颜色</div>
              <div style="text-align: center;">尺码</div>
              <div style="text-align: center;">现库存</div>
              <div style="text-align: center;">{{ currentOperation === 'in' ? '入库' : '出库' }}数量</div>
              <div style="text-align: center;">操作后</div>
            </div>
            
            <!-- SKU选项列表 -->
            <div style="max-height: 200px; overflow-y: auto;">
              <div
                v-for="sku in availableSkus"
                :key="sku.key"
                :class="['sku-row']"
                style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 12px; padding: 8px; border-radius: 4px; margin-bottom: 4px; align-items: center;"
              >
                <div style="text-align: center; font-weight: 500;">{{ sku.color }}</div>
                <div style="text-align: center; font-weight: 500;">{{ sku.size }}</div>
                <div style="text-align: center;">
                  <a-tag :color="sku.stockQuantity > 10 ? 'green' : sku.stockQuantity > 0 ? 'orange' : 'red'" style="margin: 0;">
                    {{ sku.stockQuantity }}
                  </a-tag>
                </div>
                <div style="text-align: center;">
                  <a-input-number
                    v-model:value="sku.operationQuantity"
                    :min="1"
                    :max="currentOperation === 'out' ? sku.stockQuantity : 999999"
                    size="small"
                    style="width: 80px;"
                    @change="updateSkuFinalQuantity(sku)"
                  />
                </div>
                <div style="text-align: center;">
                  <a-tag 
                    v-if="sku.operationQuantity && sku.operationQuantity > 0"
                    :color="sku.finalQuantity >= 0 ? (sku.finalQuantity > 10 ? 'green' : sku.finalQuantity > 0 ? 'orange' : 'red') : 'red'"
                    style="margin: 0;"
                  >
                    {{ sku.finalQuantity }}
                  </a-tag>
                  <span v-else style="color: #999;">-</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else style="text-align: center; color: #999; padding: 20px;">
            {{ warehouseForm.warehouseId ? '该仓库暂无库存商品' : '请先选择仓库' }}
          </div>
        </a-form-item>
        
        
        <a-form-item label="备注">
          <a-textarea
            v-model:value="warehouseForm.remark"
            placeholder="请输入备注（可选）"
            :rows="3"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { PlusOutlined, SearchOutlined } from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { productApi, warehouseApi, stockApi } from '@/api'

export default {
  name: 'ProductList',
  components: {
    PlusOutlined,
    SearchOutlined
  },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const productList = shallowRef([])  // 纯展示、整体替换，用 shallowRef 免深度响应式开销
    const categories = ref([])
    const warehouses = ref([])
    const warehouseModalVisible = ref(false)
    const modalLoading = ref(false)
    const currentOperation = ref('')
    const currentProduct = ref(null)
    const modalTitle = ref('')
    
    const searchForm = reactive({
      productIds: [],
      categoryId: undefined,
      status: undefined
    })

    const productOptions = ref([])
    const productOptionsLoading = ref(false)

    const filterProductOption = (input, option) => {
      if (!input) return true
      const label = String(option.label || '')
      return label.toLowerCase().includes(input.toLowerCase())
    }
    
    const warehouseForm = reactive({
      reasonType: undefined,
      recordDate: dayjs(),
      warehouseId: undefined,
      skuKey: undefined,
      quantity: 1,
      remark: ''
    })
    
    const availableSkus = ref([])
    const selectedSku = ref(null)
    const finalQuantity = ref(0)
    
    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `共 ${total} 条记录`
    })
    
    const columns = [
      {
        title: '商品图片',
        key: 'image',
        width: 100,
        align: 'center'
      },
      {
        title: '商品名称',
        dataIndex: 'name',
        key: 'name',
        width: 130,
        customCell: () => ({ style: { wordBreak: 'break-word', whiteSpace: 'normal' } }),
        responsive: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
      },
      {
        title: '分类',
        key: 'categoryName',
        width: 120,
        customRender: ({ record }) => {
          const category = categories.value.find(cat => cat.id === record.categoryId)
          return category ? category.name : '-'
        }
      },
      {
        title: '价格',
        key: 'price',
        width: 140
      },
      {
        title: '库存',
        key: 'stock',
        width: 200
      },
      {
        title: '总库存',
        key: 'totalStock',
        width: 80,
        align: 'center'
      },
      {
        title: '销售数',
        key: 'salesData',
        width: 120,
        align: 'center'
      },
      {
        title: '状态',
        key: 'status',
        width: 80,
        align: 'center'
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 160,
        customRender: ({ text }) => {
          return formatDateTime(text)
        }
      },
      {
        title: '操作',
        key: 'action',
        width: 240,
        fixed: 'right'
      }
    ]
    
    // 格式化日期时间函数
    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr) return '-'
      
      // 处理 yyyyMMddHHmmss 格式
      const str = dateTimeStr.toString()
      if (str.length === 14) {
        const year = str.substring(0, 4)
        const month = str.substring(4, 6)
        const day = str.substring(6, 8)
        const hour = str.substring(8, 10)
        const minute = str.substring(10, 12)
        const second = str.substring(12, 14)
        
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`
      }
      
      // 如果是时间戳格式，使用原来的处理方式
      if (str.length === 13) {
        return new Date(parseInt(str)).toLocaleString()
      }
      
      // 其他格式直接返回
      return dateTimeStr
    }
    
    const loadProducts = async () => {
      loading.value = true
      try {
        const params = {
          pageNo: pagination.current,
          pageSize: pagination.pageSize,
          productIds: searchForm.productIds && searchForm.productIds.length > 0 ? searchForm.productIds : undefined,
          categoryId: searchForm.categoryId,
          status: searchForm.status
        }

        const result = await productApi.list(params)
        productList.value = result.data.records || []
        pagination.total = result.data.total || 0
      } catch (error) {
        console.error('加载商品列表失败:', error)
      } finally {
        loading.value = false
      }
    }

    const loadProductOptions = async () => {
      productOptionsLoading.value = true
      try {
        const result = await productApi.querySimpleInfo({})
        const list = result.data || []
        productOptions.value = list.map(p => ({
          value: p.id,
          label: p.name
        }))
      } catch (error) {
        console.error('加载商品下拉选项失败:', error)
      } finally {
        productOptionsLoading.value = false
      }
    }
    
    const loadCategories = async () => {
      try {
        const result = await productApi.getCategoryTree()
        const flatCategories = []
        
        const flattenCategories = (cats, prefix = '') => {
          cats.forEach(cat => {
            flatCategories.push({
              id: cat.id,
              name: prefix + cat.name
            })
            if (cat.children && cat.children.length > 0) {
              flattenCategories(cat.children, prefix + cat.name + ' / ')
            }
          })
        }
        
        flattenCategories(result.data)
        categories.value = flatCategories
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }
    
    const loadWarehouses = async () => {
      try {
        const result = await warehouseApi.list()
        warehouses.value = result.data || []
      } catch (error) {
        console.error('加载仓库列表失败:', error)
      }
    }
    
    const disabledDate = (current) => {
      return current && current > dayjs().endOf('day')
    }
    
    const formatRecordDate = (date) => {
      return date ? dayjs(date).format('YYYYMMDD') : ''
    }
    
    const handleSearch = () => {
      pagination.current = 1
      loadProducts()
    }
    
    const handleReset = () => {
      Object.assign(searchForm, {
        productIds: [],
        categoryId: undefined,
        status: undefined
      })
      pagination.current = 1
      loadProducts()
    }
    
    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadProducts()
    }
    
    const handleEdit = (record) => {
      router.push(`/products/edit/${record.id}`)
    }
    
    
    const handleDelete = async (record) => {
      try {
        await productApi.delete({ id: record.id })
        message.success('删除成功')
        loadProducts()
      } catch (error) {
        console.error('删除失败:', error)
      }
    }
    
    const handleStockIn = async (record) => {
      currentProduct.value = record
      currentOperation.value = 'in'
      modalTitle.value = `商品入库 - ${record.name}`
      
      // 重置表单
      Object.assign(warehouseForm, {
        reasonType: 'return',
        recordDate: dayjs(),
        warehouseId: undefined,
        skuKey: undefined,
        quantity: 1,
        remark: ''
      })
      
      // 重置SKU相关数据
      availableSkus.value = []
      selectedSku.value = null
      finalQuantity.value = 0
      
      // 如果只有一个仓库，自动选中
      if (warehouses.value.length === 1) {
        warehouseForm.warehouseId = warehouses.value[0].id
        loadAvailableSkus(warehouses.value[0].id)
      }
      
      warehouseModalVisible.value = true
    }
    
    const handleStockOut = async (record) => {
      currentProduct.value = record
      currentOperation.value = 'out'
      modalTitle.value = `商品出库 - ${record.name}`
      
      // 重置表单
      Object.assign(warehouseForm, {
        reasonType: 'sales',
        recordDate: dayjs(),
        warehouseId: undefined,
        skuKey: undefined,
        quantity: 1,
        remark: ''
      })
      
      // 重置SKU相关数据
      availableSkus.value = []
      selectedSku.value = null
      finalQuantity.value = 0
      
      // 如果只有一个仓库，自动选中
      if (warehouses.value.length === 1) {
        warehouseForm.warehouseId = warehouses.value[0].id
        loadAvailableSkus(warehouses.value[0].id)
      }
      
      warehouseModalVisible.value = true
    }
    
    const loadAvailableSkus = (warehouseId) => {
      if (!currentProduct.value || !warehouseId) {
        availableSkus.value = []
        return
      }
      
      // 从商品的stockItems中筛选出当前仓库的SKU
      const skus = currentProduct.value.stockItems?.filter(
        item => item.warehouseId === warehouseId
      ) || []
      
      availableSkus.value = skus.map(item => ({
        key: `${item.warehouseId}-${item.color || 'default'}-${item.size || 'default'}`,
        label: `${item.color || '默认颜色'} / ${item.size || '默认尺码'}`,
        color: item.color || 'default',
        size: item.size || 'default',
        stockQuantity: parseInt(item.stockQuantity) || 0,
        warehouseId: item.warehouseId,
        operationQuantity: undefined,
        finalQuantity: 0
      }))
    }
    
    const onWarehouseChange = (warehouseId) => {
      warehouseForm.skuKey = undefined
      selectedSku.value = null
      finalQuantity.value = 0
      loadAvailableSkus(warehouseId)
    }
    
    const onSkuChange = (skuKey) => {
      selectedSku.value = availableSkus.value.find(sku => sku.key === skuKey) || null
      calculateFinalQuantity()
    }
    
    const selectSku = (sku) => {
      warehouseForm.skuKey = sku.key
      selectedSku.value = sku
      calculateFinalQuantity()
    }
    
    const updateSkuFinalQuantity = (sku) => {
      if (!sku.operationQuantity || sku.operationQuantity <= 0) {
        sku.finalQuantity = sku.stockQuantity
        return
      }
      
      const currentStock = parseInt(sku.stockQuantity) || 0
      const quantity = parseInt(sku.operationQuantity) || 0
      
      if (currentOperation.value === 'in') {
        sku.finalQuantity = currentStock + quantity
      } else {
        sku.finalQuantity = currentStock - quantity
      }
    }
    
    const calculateFinalQuantity = () => {
      if (!selectedSku.value || !warehouseForm.quantity) {
        finalQuantity.value = selectedSku.value ? selectedSku.value.stockQuantity : 0
        return
      }
      
      const currentStock = parseInt(selectedSku.value.stockQuantity) || 0
      const quantity = parseInt(warehouseForm.quantity) || 0
      
      if (currentOperation.value === 'in') {
        finalQuantity.value = currentStock + quantity
      } else {
        finalQuantity.value = currentStock - quantity
      }
    }
    
    const handleWarehouseConfirm = async () => {
      if (!warehouseForm.reasonType) {
        message.error('请选择操作类型')
        return
      }
      
      if (!warehouseForm.recordDate) {
        message.error('请选择记录日期')
        return
      }
      
      if (!warehouseForm.warehouseId) {
        message.error('请选择仓库')
        return
      }
      
      // 检查是否有SKU输入了数量
      const operationSkus = availableSkus.value.filter(sku => sku.operationQuantity && sku.operationQuantity > 0)
      
      if (operationSkus.length === 0) {
        message.error('请至少为一个SKU输入操作数量')
        return
      }
      
      // 检查出库数量是否超过库存
      if (currentOperation.value === 'out') {
        const invalidSkus = operationSkus.filter(sku => sku.operationQuantity > sku.stockQuantity)
        if (invalidSkus.length > 0) {
          message.error('出库数量不能超过当前库存')
          return
        }
      }
      
      modalLoading.value = true
      try {
        const operation = currentOperation.value === 'in' ? '入库' : '出库'
        
        // 构建请求数据，支持多个SKU
        const requestData = {
          reasonType: warehouseForm.reasonType,
          recordDate: formatRecordDate(warehouseForm.recordDate),
          productId: currentProduct.value.id,
          warehouseId: warehouseForm.warehouseId,
          remark: warehouseForm.remark,
          items: operationSkus.map(sku => ({
            productId: currentProduct.value.id,
            warehouseId: warehouseForm.warehouseId,
            color: sku.color,
            size: sku.size,
            quantity: sku.operationQuantity
          }))
        }
        
        console.log(`${operation}请求数据:`, requestData)
        
        // 调用相应的入库/出库API
        if (currentOperation.value === 'in') {
          await stockApi.stockIn(requestData)
        } else {
          await stockApi.stockOut(requestData)
        }
        
        const totalQuantity = operationSkus.reduce((sum, sku) => sum + sku.operationQuantity, 0)
        message.success(`${operation}操作成功 - 共${operationSkus.length}个SKU，总数量：${totalQuantity}`)
        
        warehouseModalVisible.value = false
        loadProducts() // 重新加载商品列表以更新库存信息
      } catch (error) {
        console.error('操作失败:', error)
        message.error(`操作失败: ${error.message || '未知错误'}`)
      } finally {
        modalLoading.value = false
      }
    }
    
    onMounted(() => {
      loadProducts()
      loadCategories()
      loadWarehouses()
      loadProductOptions()
    })

    // 商品列表"库存"列：把 stockItems 按 颜色+尺码 跨仓库聚合
    const aggregateStock = (stockItems) => {
      const map = new Map()
      ;(stockItems || []).forEach(it => {
        const key = `${it.color || ''}|${it.size || ''}`
        const cur = map.get(key)
        const q = Number(it.stockQuantity) || 0
        const p = Number(it.producingQuantity) || 0
        if (cur) { cur.stockQuantity += q; cur.producingQuantity += p }
        else map.set(key, { color: it.color || '', size: it.size || '', stockQuantity: q, producingQuantity: p })
      })
      return Array.from(map.values())
    }

    return {
      loading,
      productList,
      categories,
      warehouses,
      warehouseModalVisible,
      modalLoading,
      currentOperation,
      modalTitle,
      searchForm,
      warehouseForm,
      availableSkus,
      selectedSku,
      finalQuantity,
      pagination,
      columns,
      productOptions,
      productOptionsLoading,
      filterProductOption,
      handleSearch,
      handleReset,
      handleTableChange,
      handleEdit,
      handleDelete,
      handleStockIn,
      handleStockOut,
      onWarehouseChange,
      onSkuChange,
      selectSku,
      updateSkuFinalQuantity,
      calculateFinalQuantity,
      handleWarehouseConfirm,
      formatDateTime,
      disabledDate,
      formatRecordDate,
      aggregateStock
    }
  }
}
</script>

<style scoped>
.product-list {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}

.no-image {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
  color: #999;
}

.sku-row {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.sku-row:hover {
  background-color: #f5f5f5;
  border-color: #d9d9d9;
}

.sku-row.selected {
  background-color: #e6f4ff;
  border-color: #1890ff;
}
</style>